import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Award, Heart, ChevronRight, Star, ArrowRight } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

export default function Home() {
  const [prestationsPhares, setPrestationsPhares] = useState([]);
  const [temoignages, setTemoignages] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleRating, setGoogleRating] = useState(null);
  const [googleTotal, setGoogleTotal] = useState(0);
  const [avantApres, setAvantApres] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prestationsRes, temoignagesRes, avantApresRes, contentRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prestations`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/temoignages`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avant-apres`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-content`)
      ]);

      const prestations = await safeJson(prestationsRes, 'prestations');
      if (Array.isArray(prestations)) setPrestationsPhares(prestations.slice(0, 3));

      const temoignagesData = await safeJson(temoignagesRes, 'temoignages');
      if (Array.isArray(temoignagesData)) setTemoignages(temoignagesData.slice(0, 3));

      // Avis Google
try {
  const googleRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-reviews`);
  if (googleRes.ok) {
    const googleData = await googleRes.json();
    setGoogleReviews(googleData.reviews || []);
    setGoogleRating(googleData.rating);
    setGoogleTotal(googleData.total);
  }
} catch (e) {
  console.log('Google reviews non disponibles');
}

      const avantApresData = await safeJson(avantApresRes, 'avant-apres');
      if (Array.isArray(avantApresData)) setAvantApres(avantApresData.slice(0, 3));

      const contentData = await safeJson(contentRes, 'homepage-content');
      setContent(contentData || {});
    } catch (error) {
      console.error('Erreur fetchData:', error);
      setContent({});
    } finally {
      setLoading(false);
    }
  };

  const safeJson = async (response, label) => {
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.warn(`[${label}] HTTP ${response.status}`, text?.slice?.(0, 200));
      return null;
    }
    return response.json();
  };

  const faqItems = Array.isArray(content?.faq_items) ? content.faq_items : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          {content.hero_image && (
            <img
              src={content.hero_image}
              alt="Hero"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background"></div>
        </div>
        <div className="relative z-10 container-custom text-center text-white px-4">
          {content.hero_titre && (
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif mb-4 sm:mb-6"
            >
              {content.hero_titre}
            </motion.h1>
          )}
          {content.hero_sous_titre && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-sm sm:text-base lg:text-lg max-w-2xl mx-auto mb-6 sm:mb-8 text-gray-200 px-4 whitespace-pre-line"
            >
              {content.hero_sous_titre}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link
              to="/reservation"
              data-testid="hero-cta-button"
              className="inline-flex items-center btn-primary px-6 sm:px-8 py-2.5 sm:py-3 bg-accent text-accent-foreground rounded-sm font-medium space-x-2 hover:shadow-hover text-sm sm:text-base"
            >
              <span>Réserver maintenant</span>
              <ChevronRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Prestations Phares */}
      {prestationsPhares.length > 0 && (
        <section data-testid="featured-services-section" className="py-12 sm:py-16 md:py-24">
          <div className="container-custom">
            <motion.div {...fadeInUp} className="text-center mb-8 sm:mb-12 px-4">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif mb-3 sm:mb-4">Nos Prestations Phares</h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
                Découvrez une sélection de nos soins les plus prisés
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-0">
              {prestationsPhares.map((prestation, index) => (
                <motion.div
                  key={prestation.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`featured-service-card-${index}`}
                  className="group bg-white border border-border rounded-sm overflow-hidden hover-lift"
                >
                  <div className="p-4 sm:p-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/10 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <Sparkles className="text-accent" size={20} />
                    </div>
                    <h3 className="text-lg sm:text-xl font-serif mb-2">{prestation.nom}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 line-clamp-2 whitespace-pre-line">{prestation.description}</p>
                    <div className="flex items-center justify-between mb-3 sm:mb-0">
                      <span className="text-xl sm:text-2xl font-serif text-accent">{prestation.prix_euros}€</span>
                      <span className="text-xs sm:text-sm text-muted-foreground">{prestation.duree_minutes} min</span>
                    </div>
                  </div>
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <Link
                      to="/reservation"
                      data-testid={`service-book-button-${index}`}
                      className="block text-center btn-primary px-4 py-2 bg-accent text-accent-foreground rounded-sm text-sm font-medium"
                    >
                      Réserver
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div {...fadeInUp} className="text-center mt-8 sm:mt-12">
              <Link
                to="/prestations"
                data-testid="view-all-services-link"
                className="inline-flex items-center text-accent hover:underline font-medium space-x-2 text-sm sm:text-base"
              >
                <span>Voir toutes nos prestations</span>
                <ChevronRight size={20} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Avant/Après Section */}
      {avantApres.length > 0 && (
        <section data-testid="avant-apres-preview-section" className="py-12 sm:py-16 md:py-24 bg-secondary/30">
          <div className="container-custom">
            <motion.div {...fadeInUp} className="text-center mb-8 sm:mb-12 px-4">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif mb-3 sm:mb-4">Nos Transformations</h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
                Découvrez les résultats de nos soins experts
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-0">
              {avantApres.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`avant-apres-preview-${index}`}
                  className="bg-white border border-border rounded-sm overflow-hidden hover-lift"
                >
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img
                        src={item.image_avant}
                        alt={`${item.titre} - Avant`}
                        className="w-full h-32 sm:h-40 object-cover"
                      />
                      <span className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        Avant
                      </span>
                    </div>
                    <div className="relative">
                      <img
                        src={item.image_apres}
                        alt={`${item.titre} - Après`}
                        className="w-full h-32 sm:h-40 object-cover"
                      />
                      <span className="absolute bottom-2 left-2 bg-accent text-accent-foreground text-xs px-2 py-1 rounded">
                        Après
                      </span>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="font-serif text-base sm:text-lg">{item.titre}</h3>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div {...fadeInUp} className="text-center mt-8 sm:mt-12">
              <Link
                to="/avant-apres"
                data-testid="view-all-transformations-link"
                className="inline-flex items-center text-accent hover:underline font-medium space-x-2 text-sm sm:text-base"
              >
                <span>Voir toutes nos transformations</span>
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* À Propos */}
      {(content.about_titre || content.about_texte || content.about_image) && (
        <section data-testid="about-section" className="py-12 sm:py-16 md:py-24" style={{ backgroundColor: '#F2F2F2' }}>
          <div className="container-custom px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
              {content.about_image && (
                <motion.div {...fadeInUp}>
                  <img
                    src={content.about_image}
                    alt="Institut"
                    className="rounded-sm shadow-soft w-full"
                  />
                </motion.div>
              )}
              <motion.div {...fadeInUp} className="px-4 sm:px-0">
                {content.about_titre && (
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif mb-4 sm:mb-6">{content.about_titre}</h2>
                )}
                {content.about_texte && (
                  <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 whitespace-pre-line">
                    {content.about_texte}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Avis */}
      {(googleReviews.length > 0 || temoignages.length > 0) && (
  <section data-testid="testimonials-section" className="py-12 sm:py-16 md:py-24 bg-secondary/30">
    <div className="container-custom">
      <motion.div {...fadeInUp} className="text-center mb-8 sm:mb-12 px-4">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif mb-3 sm:mb-4">Ce Que Disent Nos Clientes</h2>
        {googleRating && (
          <div className="flex items-center justify-center space-x-2 mt-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="text-accent fill-accent" />
              ))}
            </div>
            <span className="font-semibold text-lg">{googleRating}/5</span>
            <span className="text-muted-foreground text-sm">({googleTotal} avis Google)</span>
          </div>
        )}
        <p className="text-sm sm:text-base text-muted-foreground mt-2">Votre satisfaction est notre fierté</p>
      </motion.div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-4 sm:px-0">
        {(googleReviews.length > 0 ? googleReviews : temoignages).map((review, index) => {
          const isGoogle = googleReviews.length > 0;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              data-testid={`testimonial-card-${index}`}
              className="bg-white border border-border rounded-sm p-4 sm:p-6 flex flex-col"
            >
              {isGoogle && (
                <div className="flex items-center space-x-3 mb-3">
                  <img
                    src={review.profile_photo_url}
                    alt={review.author_name}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div>
                    <p className="font-medium text-sm">{review.author_name}</p>
                    <p className="text-xs text-muted-foreground">{review.relative_time_description}</p>
                  </div>
                  <div className="ml-auto">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                </div>
              )}
              <div className="flex mb-3">
                {[...Array(isGoogle ? review.rating : (review.note || 5))].map((_, i) => (
                  <Star key={i} size={14} className="text-accent fill-accent" />
                ))}
              </div>
              <p className="text-muted-foreground italic text-sm flex-grow">
                "{isGoogle ? review.text : review.texte}"
              </p>
              {!isGoogle && (
                <p className="font-medium text-sm mt-3">{review.nom}</p>
              )}
              {isGoogle && (
                <a
                  href={review.author_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground mt-3 hover:text-accent transition-colors"
                >
                  Voir sur Google →
                </a>
              )}
            </motion.div>
          );
        })}
      </div>
      {googleTotal > 5 && (
        <div className="text-center mt-8">
          <a
            href="https://www.google.com/maps/place/?q=place_id:ChIJ2V56LB_nDqUR0x40UEdQxnc"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-sm text-accent hover:underline"
          >
            <span>Voir tous les {googleTotal} avis sur Google</span>
          </a>
        </div>
      )}
    </div>
  </section>
)}

      {/* FAQ - depuis la BDD */}
      {faqItems.length > 0 && (
        <section data-testid="faq-section" className="py-12 sm:py-16 md:py-24">
          <div className="container-custom max-w-4xl px-4">
            <motion.div {...fadeInUp} className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif mb-3 sm:mb-4">Questions Fréquentes</h2>
              <p className="text-sm sm:text-base text-muted-foreground">Tout ce que vous devez savoir</p>
            </motion.div>

            <div className="space-y-3 sm:space-y-4">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  data-testid={`faq-item-${index}`}
                  className="bg-white border border-border rounded-sm p-4 sm:p-6"
                >
                  <h3 className="font-medium text-base sm:text-lg mb-2">{item.question}</h3>
                  <p className="text-muted-foreground text-sm sm:text-base whitespace-pre-line">{item.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Final */}
      {(content.cta_titre || content.cta_texte) && (
        <section data-testid="final-cta-section" className="py-12 sm:py-16 md:py-24 bg-secondary/30">
          <div className="container-custom">
            <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto px-4">
              {content.cta_titre && (
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif mb-4 sm:mb-6">{content.cta_titre}</h2>
              )}
              {content.cta_texte && (
                <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8 whitespace-pre-line">
                  {content.cta_texte}
                </p>
              )}
              <Link
                to="/reservation"
                data-testid="final-cta-button"
                className="inline-flex items-center btn-primary px-6 sm:px-8 py-2.5 sm:py-3 bg-accent text-accent-foreground rounded-sm font-medium space-x-2 hover:shadow-hover text-sm sm:text-base"
              >
                <span>Réserver en ligne</span>
                <ChevronRight size={20} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}
